import create, { UseStore } from 'zustand';
import { userProfileApi, UserProfileApi } from '../api/user-profile-api';

import { fetchRequest } from './';
import { showToast } from './toast';

export interface NameCode {
  name: string;
  code?: string;
}
export interface DepartmentRole {
  code: string;
  doctorId?: string;
  name?: string;
}
export interface LocationRole extends NameCode {
  departments?: DepartmentRole[];
}

export interface OrganizationRole extends NameCode {
  locations: LocationRole[];
}
export interface OrganizationSelection {
  organization: OrganizationRole;
  location?: LocationRole;
  department?: DepartmentRole;
}

export type OrganizationApi = {
  organizations: OrganizationRole[];
  selection: OrganizationSelection;
  pendingOrComplete: boolean;
  promptOrg: boolean;
  setSelection(value: OrganizationSelection): void;
  setSelectionByCode(orgCode: string, locCode?: string, depCode?: string): void;
  loadOrganizations(activity: string): Promise<void>;
  setUpdateFn(fn: (a?: OrganizationSelection) => void): void;
  findDepartment(code: string): DepartmentRole;
};

export type OrganizationScope = {
  organizationCode: string;
  locationCode: string;
  departmentCode: string;
};
//TODO refactor selection UI to use this lighter weight representation
export const organizationScope = create<OrganizationScope>(() => ({
  organizationCode: null,
  locationCode: null,
  departmentCode: null,
}));

const lastScope: OrganizationScope = {
  organizationCode: '',
  locationCode: '',
  departmentCode: '',
};

export function organizationSelectionApi(
  profile: UserProfileApi
): UseStore<OrganizationApi> {
  let update: (a?: OrganizationSelection) => void;

  return create<OrganizationApi>((set, get) => ({
    organizations: [],
    selection: null,
    pendingOrComplete: false,
    promptOrg: false,
    setSelection: (selection) => {
      set({ selection });

      let organizationCode = selection?.organization?.code;
      let locationCode = selection?.location?.code;
      let departmentCode = selection?.department?.code;

      if (organizationCode && locationCode && departmentCode) {
        //only trigger if all are valid and changed
        if (
          lastScope.organizationCode !== organizationCode ||
          lastScope.locationCode !== locationCode ||
          lastScope.departmentCode !== departmentCode
        ) {
          organizationScope.setState({
            organizationCode: organizationCode,
            locationCode,
            departmentCode,
          });

          lastScope.organizationCode = organizationCode;
          lastScope.locationCode = locationCode;
          lastScope.departmentCode = departmentCode;
        }
      }
    },
    loadOrganizations: async (activity: string) => {
      set({ pendingOrComplete: true });

      const cached = get().organizations;
      let organizations = cached?.length
        ? cached
        : ((userProfileApi &&
            userProfileApi.organizationsByActivity(activity || 'CODING')) as any);

      if (!organizations?.length) {
        try {

          if(userProfileApi){
            await userProfileApi.load();
            organizations = userProfileApi.organizationsByActivity(activity || 'CODING');
          }

        } catch (e) {
          organizations = [];
          showToast(
            'This user does not appear to have any Presidio Roles. Please contact an administrator for access.'
          );
        }
      }
      set({ organizations });

      return Promise.resolve();
    },
    setUpdateFn: (fn) => {
      update = fn;
    },
    findDepartment: (code) => {
      const s = get().selection;
      if(s.location?.departments?.length){
        return s.location.departments.find((a) => a.code === code);
      }
      return null;
    },
    setSelectionByCode: (
      orgCode?: string,
      locCode?: string,
      depCode?: string
    ) => {
      const organizations = get().organizations;

      function setFields(organization: any) {
        let location: LocationRole;
        let department: DepartmentRole;
        if (organization) {
          if (organization.locations?.length && locCode) {
            location = organization.locations.filter(
              (a) => a.code === locCode
            )[0];
            if (location && depCode) {
              if (location.departments?.length) {
                department = location.departments.filter(
                  (a) => a.code == depCode
                )[0];
              }
            }
          }

          const selection = { organization, location, department };
          set({ selection });

          if (update) {
            update(selection);
          }
        } else {
          console.log(
            'setSelectionByCode: org not found',
            orgCode,
            organizations
          );
        }
      }

      if (organizations?.length) {
        const organization = orgCode
          ? organizations.filter((a) => a.code === orgCode)[0]
          : null;
        if (organization) {
          setFields(organization);
        } else {
          setFields(organizations[organizations.length - 1]);
        }
      }
    },
  }));
}
export const organizationApi = organizationSelectionApi(userProfileApi);
